/* SUBTERRA browser client: connects to the one server-side world, mirrors it, and draws it.
   The browser decides nothing about the world; it only interpolates what the server reports. */
import { W, CH, SURF, M_PER_PX, LAYERS, layerAtM, ORE_BY_ID, CLASSES, PROTOCOL_VERSION } from '../shared/config';
import { decodeChunk, decodeRobots, BIN_CHUNK, BIN_ROBOTS } from '../shared/protocol';
import { NS_EDGE, chamberRx } from '../shared/graph';
import { S, CRobot } from './state';
import { buildSprites } from './sprites';
import { initRender, layoutCanvas, render, drawRail, view, depositById } from './render';
import { ui, initUI, toast, feedPush, inspect } from './ui';
import { initLaunch } from './launch';

const $ = (id: string) => document.getElementById(id)!;
const viewEl = $('view'), cv = $('cv') as HTMLCanvasElement, railCv = $('rail').querySelector('canvas') as HTMLCanvasElement;

/* ---------------- network ---------------- */
let ws: WebSocket | null = null, retry = 800, pendingChunks = new Map<number, number>();
function connect() {
  ws = new WebSocket((location.protocol === 'https:' ? 'wss://' : 'ws://') + location.host + '/ws');
  ws.binaryType = 'arraybuffer';
  ws.onopen = () => { S.connected = true; ui.conn(true); lastViewSig = ''; };
  ws.onclose = () => {
    S.connected = false; ui.conn(false);
    /* on reconnect the snapshot rebuilds the graph and every chunk bitmap is fetched fresh */
    S.world.invalidateDug(); pendingChunks.clear();
    setTimeout(connect, retry); retry = Math.min(8000, retry * 1.6);
  };
  ws.onmessage = e => {
    if (typeof e.data !== 'string') {
      const kind = new Uint8Array(e.data)[0];
      if (kind === BIN_CHUNK) { const c = decodeChunk(e.data, W * CH); pendingChunks.delete(c.k); S.world.applyChunk(c.k, c.ver, c.dug); }
      else if (kind === BIN_ROBOTS) applyRobots(e.data);
      return;
    }
    const m = JSON.parse(e.data);
    if (m.t === 'snap') applySnap(m); else if (m.t === 'd') applyDelta(m);
  };
}
function send(o: unknown) { if (ws && ws.readyState === 1) ws.send(JSON.stringify(o)); }

function addRobot(meta: { id: number; c: number; n: string; b: number; o?: string; d?: number }) {
  let r = S.robots.get(meta.id);
  if (!r) { r = { id: meta.id, cls: meta.c, name: meta.n, born: meta.b, x: 0, y: 0, fx: 0, fy: 0, tx: 0, ty: 0, t0: 0, st: 0, left: false, carry: 0, bat: 100, seen: false, step: 0 }; S.robots.set(meta.id, r); }
  else { r.cls = meta.c; r.name = meta.n; }
  if (meta.o) { r.owner = meta.o; r.demo = !!meta.d; }
  return r;
}
function applySnap(m: any) {
  retry = 800; /* backoff resets only once the server actually delivered a world */
  /* the server was upgraded under an open tab: reload once (bundles are cache-busted per build) */
  if (m.v !== PROTOCOL_VERSION) {
    const q = new URLSearchParams(location.search);
    if (q.get('pv') !== String(m.v)) { q.set('pv', String(m.v)); location.replace(location.pathname + '?' + q.toString()); return; }
    toast({ k: 'sys', m: 'this page is out of date: hard-refresh to update' } as any);
  }
  S.world.setSeed(m.seed); S.created = m.created;
  S.world.nodes = [];
  for (const r of m.nodes) S.world.applyNode(r);
  const keep = new Set<number>();
  for (const r of m.robots) { addRobot(r); keep.add(r.id); }
  for (const id of [...S.robots.keys()]) if (!keep.has(id)) S.robots.delete(id);
  S.world.heap = Float32Array.from(m.heap);
  S.stats = m.g; S.ops = m.ops; S.events = m.events; S.blocks = m.blocks; S.chambers = m.chambers; S.discoveries = m.discoveries;
  S.known = new Set(m.known); S.breached = new Set(m.breached); S.skillLog = m.skillLog; S.layers = m.layers;
  if (!S.world.ready) {
    S.world.ready = true; jumpTo(SURF + 60, true, W / 2); /* centre the shaft on narrow screens */
    /* deep link from the agents page: /?follow=<id> */
    const f = +(new URLSearchParams(location.search).get('follow') || 0);
    if (f && S.robots.has(f)) select({ kind: 'robot', id: f }, true);
  }
  ui.all(); ui.feedAll();
}
function applyDelta(m: any) {
  if (m.n) for (const r of m.n) {
    const n = S.world.applyNode(r);
    if (n.state === NS_EDGE && n.carved > 1) { const h = n.pts[Math.min(n.pts.length - 1, n.prog)]; spark(h[0], h[1], 1); }
  }
  if (m.j) for (const r of m.j) addRobot(r);
  if (m.x) for (const id of m.x) { S.robots.delete(id); if (S.sel?.kind === 'robot' && S.sel.id === id) { S.sel = null; S.follow = false; } }
  if (m.e) for (const ev of m.e) { S.events.push(ev); feedPush(ev); if (['skill', 'layer', 'disc', 'build'].includes(ev.k) && (ev.k !== 'build' || /online/.test(ev.m))) toast(ev); }
  if (S.events.length > 400) S.events.splice(0, S.events.length - 400);
  if (m.b) { S.blocks.unshift(...m.b.reverse()); S.blocks.length = Math.min(S.blocks.length, 30); }
  if (m.heap) S.world.heap = Float32Array.from(m.heap);
  if (m.fx) for (const f of m.fx) spark(f[0], f[1], f[2]);
  if (m.g) { S.stats = m.g; S.ops = m.ops; S.chambers = m.chambers; if (m.disc) S.discoveries = m.disc; S.known = new Set(m.known); S.breached = new Set(m.breached); S.skillLog = m.skillLog; S.layers = m.layers; ui.stats(); }
  if (m.det !== undefined) { S.det = m.det; }
}
function applyRobots(buf: ArrayBuffer) {
  const { full, list } = decodeRobots(buf), now = performance.now();
  if (S.lastFrame) S.frameDt = Math.min(.5, Math.max(.1, S.frameDt * .8 + (now - S.lastFrame) / 1000 * .2));
  S.lastFrame = now;
  const got = new Set<number>();
  for (const p of list) {
    let r = S.robots.get(p.id);
    if (!r) r = addRobot({ id: p.id, c: p.cls, n: 'UNIT-' + p.id, b: 0 });
    got.add(p.id);
    const jump = !r.seen || Math.abs(p.x - r.x) + Math.abs(p.y - r.y) > 60;
    r.fx = jump ? p.x : r.x; r.fy = jump ? p.y : r.y; r.tx = p.x; r.ty = p.y; r.t0 = now;
    if (jump) { r.x = p.x; r.y = p.y; }
    r.cls = p.cls; r.st = p.st; r.left = p.left; r.carry = p.carry; r.bat = p.bat; r.seen = true;
  }
  if (full) for (const r of S.robots.values()) if (!got.has(r.id)) r.seen = false;
}

/* ---------------- particles ---------------- */
function spark(x: number, y: number, kind: number) {
  const mat = S.world.terrain ? S.world.matAt(x, y) : 0, ore = ORE_BY_ID[mat];
  for (let k = 0; k < (kind === 2 ? 4 : 2); k++) {
    const dust = Math.random() < .6;
    S.parts.push({ x: x + (Math.random() - .5) * 4, y: y + (Math.random() - .5) * 3, vx: (Math.random() - .5) * (dust ? 8 : 22), vy: -Math.random() * (dust ? 5 : 14), life: dust ? .7 : .3, max: dust ? .7 : .3, col: ore ? ore.hi : dust ? '#8a8272' : '#ffd27a', g: dust ? 8 : 30 });
  }
}

/* ---------------- camera ---------------- */
const worldBottom = () => Math.max(SURF + 300, S.world.deepestY() + 200);
function clampCam() {
  const vh = view.vh || 400, vw = view.vw || W;
  S.cam.y = Math.max(-20, Math.min(worldBottom() - vh * .5, S.cam.y));
  S.cam.x = vw >= W ? 0 : Math.max(0, Math.min(W - vw + 1, S.cam.x));
}
export function jumpTo(y: number, instant = false, x?: number) {
  S.follow = false; ui.follow();
  const ty = y - view.vh / 2, tx = x !== undefined ? x - view.vw / 2 : S.cam.x;
  if (instant) { S.cam.y = ty; S.cam.x = tx; S.cam.ty = S.cam.tx = null; clampCam(); }
  else { S.cam.ty = ty; S.cam.tx = tx; }
}
function setZoom(s: number, ax?: number, ay?: number) {
  s = Math.max(1, Math.min(6, s)); if (s === S.cam.s) return;
  const rect = viewEl.getBoundingClientRect();
  const px = ax ?? rect.width / 2, py = ay ?? rect.height / 2;
  const wx = view.x0 + px / S.cam.s, wy = S.cam.y + py / S.cam.s;
  S.cam.s = s; layoutCanvas(viewEl);
  S.cam.x = wx - px / s; S.cam.y = wy - py / s; clampCam();
  $('z-lvl').textContent = s + '×';
}

/* ---------------- input ---------------- */
let drag: null | { x: number; y: number; cx: number; cy: number; moved: boolean } = null;
viewEl.addEventListener('pointerdown', e => { drag = { x: e.clientX, y: e.clientY, cx: S.cam.x, cy: S.cam.y, moved: false }; viewEl.setPointerCapture(e.pointerId); });
viewEl.addEventListener('pointermove', e => {
  const r = viewEl.getBoundingClientRect(); S.hover = { x: view.x0 + (e.clientX - r.left) / S.cam.s, y: S.cam.y + (e.clientY - r.top) / S.cam.s };
  if (!drag) return;
  const dx = e.clientX - drag.x, dy = e.clientY - drag.y;
  if (Math.abs(dx) + Math.abs(dy) > 4) { drag.moved = true; viewEl.classList.add('drag'); if (S.follow) { S.follow = false; ui.follow(); } }
  if (drag.moved) { S.cam.x = drag.cx - dx / S.cam.s; S.cam.y = drag.cy - dy / S.cam.s; S.cam.ty = S.cam.tx = null; clampCam(); }
});
viewEl.addEventListener('pointerup', e => {
  viewEl.classList.remove('drag');
  if (drag && !drag.moved) pick(e);
  drag = null;
});
viewEl.addEventListener('pointerleave', () => { S.hover = null; });
viewEl.addEventListener('wheel', e => {
  e.preventDefault();
  if (e.ctrlKey || e.metaKey) { const r = viewEl.getBoundingClientRect(); setZoom(S.cam.s + (e.deltaY < 0 ? 1 : -1), e.clientX - r.left, e.clientY - r.top); return; }
  if (S.follow) { S.follow = false; ui.follow(); }
  S.cam.y += e.deltaY / S.cam.s; S.cam.x += e.deltaX / S.cam.s; S.cam.ty = S.cam.tx = null; clampCam();
}, { passive: false });
window.addEventListener('keydown', e => {
  if ((e.target as HTMLElement).tagName === 'INPUT') return;
  const step = 40 / S.cam.s;
  if (e.key === 'ArrowDown' || e.key === 's') { S.cam.y += step; S.cam.ty = null; }
  else if (e.key === 'ArrowUp' || e.key === 'w') { S.cam.y -= step; S.cam.ty = null; }
  else if (e.key === 'ArrowLeft' || e.key === 'a') { S.cam.x -= step; S.cam.tx = null; }
  else if (e.key === 'ArrowRight' || e.key === 'd') { S.cam.x += step; S.cam.tx = null; }
  else if (e.key === '+' || e.key === '=') setZoom(S.cam.s + 1);
  else if (e.key === '-') setZoom(S.cam.s - 1);
  else if (e.key === 'Home') jumpTo(SURF + 40);
  else if (e.key === 'End') jumpDeepest();
  else if (e.key === 'f') toggleFollow();
  else if (e.key === 'Escape') select(null);
  else return;
  clampCam();
});
$('z-in').onclick = () => setZoom(S.cam.s + 1);
$('z-out').onclick = () => setZoom(S.cam.s - 1);
$('j-surf').onclick = () => jumpTo(SURF + 40);
$('j-deep').onclick = () => jumpDeepest();
$('j-follow').onclick = () => toggleFollow();
$('rail').addEventListener('click', e => {
  const r = $('rail').getBoundingClientRect(), deep = Math.max(SURF + 200, S.world.deepestY() + 120);
  jumpTo((e.clientY - r.top) / r.height * deep, false, (e.clientX - r.left) / r.width * W);
});
function jumpDeepest() {
  let best = null as any;
  for (const n of S.world.nodes) if (n && n.state === NS_EDGE && (!best || n.pts[n.prog][1] > best.pts[best.prog][1])) best = n;
  if (best) { const h = best.pts[best.prog]; jumpTo(h[1], false, h[0]); } else jumpTo(S.world.deepestY());
}
function toggleFollow() {
  if (S.sel?.kind !== 'robot') { toast({ k: 'sys', m: 'select a unit first: click any robot in the viewport' } as any); return; }
  S.follow = !S.follow; ui.follow();
}
export function select(sel: typeof S.sel, follow = false) {
  S.sel = sel; S.det = null;
  if (follow && sel?.kind === 'robot') { S.follow = true; const r = S.robots.get(sel.id); if (r && !r.seen) S.follow = true; }
  else if (!follow) S.follow = S.follow && sel?.kind === 'robot';
  ui.follow(); inspect();
  lastViewSig = '';
}
function pick(e: PointerEvent) {
  const rect = viewEl.getBoundingClientRect(), wx = view.x0 + (e.clientX - rect.left) / S.cam.s, wy = S.cam.y + (e.clientY - rect.top) / S.cam.s;
  let best: CRobot | null = null, bd = 6;
  for (const r of S.robots.values()) { if (!r.seen) continue; const d = Math.hypot(r.x - wx, r.y - 1 - wy); if (d < bd) { bd = d; best = r; } }
  if (best) return select({ kind: 'robot', id: best.id });
  for (const n of S.world.nodes) {
    if (!n || !n.type || n.cp <= 0) continue;
    const rx = chamberRx(n.r) + 1, ry = n.r + 1;
    if (((wx - n.x) / rx) ** 2 + ((wy - n.y) / ry) ** 2 <= 1) return select({ kind: 'chamber', id: n.id });
  }
  const t = S.world.terrain, k = Math.floor(wy / CH);
  for (let kk = k - 1; kk <= k + 1; kk++) for (const d of kk >= 0 ? t.deposits(kk) : []) {
    if (!S.known.has(d.id) && !S.breached.has(d.id)) continue;
    if (((wx - d.x) / (d.rx + 2)) ** 2 + ((wy - d.y) / (d.ry + 2)) ** 2 <= 1) return select({ kind: 'deposit', id: d.id });
  }
  if (wx >= 0 && wx < W && wy > SURF - 40) return select({ kind: 'rock', x: Math.round(wx), y: Math.round(wy) });
  select(null);
}

/* ---------------- view sync ---------------- */
let lastViewSig = '', lastViewT = 0;
function syncView(now: number) {
  const y0 = Math.floor(S.cam.y) - 30, y1 = y0 + view.vh + 60, sel = S.sel?.kind === 'robot' ? S.sel.id : 0;
  const sig = Math.round(y0 / 8) + '|' + Math.round(y1 / 8) + '|' + sel;
  if (sig !== lastViewSig && now - lastViewT > 120) { lastViewSig = sig; lastViewT = now; send({ t: 'view', y0, y1, sel }); }
  /* chunk bitmaps for what is (nearly) on screen */
  const k0 = Math.max(0, Math.floor((S.cam.y - 60) / CH)), k1 = Math.floor((S.cam.y + view.vh + 60) / CH), want: number[] = [];
  for (let k = k0; k <= k1; k++) {
    const c = S.world.chunks.get(k);
    if ((!c || c.state !== 'ready') && (!pendingChunks.has(k) || now - pendingChunks.get(k)! > 4000)) { want.push(k); pendingChunks.set(k, now); }
  }
  if (want.length && S.connected) send({ t: 'chunks', ks: want });
  S.world.evict(k0 - 2, k1 + 2);
}

/* ---------------- frame loop ---------------- */
let last = performance.now(), uiT = 0, railT = 0;
function loop(now: number) {
  const dt = Math.min(.1, (now - last) / 1000); last = now;
  layoutCanvas(viewEl);
  for (const r of S.robots.values()) {
    if (!r.seen) continue;
    const k = Math.min(1, (now - r.t0) / 1000 / S.frameDt), nx = r.fx + (r.tx - r.fx) * k, ny = r.fy + (r.ty - r.fy) * k;
    if (nx !== r.x || ny !== r.y) { r.step += dt * 9; r.x = nx; r.y = ny; }
  }
  for (const p of S.parts) { p.x += p.vx * dt; p.y += p.vy * dt; p.vy += p.g * dt; p.life -= dt; }
  if (S.parts.length) { let j = 0; for (const p of S.parts) if (p.life > 0) S.parts[j++] = p; S.parts.length = Math.min(j, 700); }
  if (S.follow && S.sel?.kind === 'robot') {
    const r = S.robots.get(S.sel.id);
    if (r && r.seen) { S.cam.y += (r.y - view.vh / 2 - S.cam.y) * .12; if (view.vw < W) S.cam.x += (r.x - view.vw / 2 - S.cam.x) * .12; }
  } else if (S.cam.ty !== null) {
    S.cam.y += (S.cam.ty - S.cam.y) * .14; if (S.cam.tx !== null) S.cam.x += (S.cam.tx - S.cam.x) * .14;
    if (Math.abs(S.cam.ty - S.cam.y) < .5) S.cam.ty = S.cam.tx = null;
  }
  clampCam();
  if (S.world.ready) { syncView(now); render(now); }
  if (now - uiT > 250) { uiT = now; ui.hud(); inspect(); }
  if (now - railT > 700 && S.world.ready) { railT = now; const deep = drawRail(railCv); $('rail-lbl').textContent = ((deep - SURF) * M_PER_PX).toFixed(0) + ' m'; }
  requestAnimationFrame(loop);
}

/* ---------------- boot ---------------- */
buildSprites();
initRender(cv);
initUI({ jumpTo, select });
initLaunch(select);
S.cam.s = Math.max(1, Math.min(4, Math.floor(viewEl.clientWidth / W) || 1));
$('z-lvl').textContent = S.cam.s + '×';
layoutCanvas(viewEl);
connect();
requestAnimationFrame(loop);
window.addEventListener('resize', () => layoutCanvas(viewEl));
export { LAYERS, layerAtM, CLASSES };
/* read-only handle for debugging in the browser console */
(window as any).__subterra = { S, view, select, jumpTo };
